import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../utils/echarts'
import styled from 'styled-components'
import { Box } from '../../atoms/Box'
import { Text } from '../../atoms/Text'
import { Loading } from '../../atoms/Loading'
import { Button } from '../../atoms/Button'
import { ButtonGroup } from '../../molecules/ButtonGroup'
import { StatsContainer } from '../../molecules/StatsContainer'
import {
  ChartContainer,
  ChartGrid,
  ChartSection,
  ChartTitle,
  ErrorContainer,
  NotAvailable,
  PrintWrapper,
} from '../../molecules/ChartComponents'
import { DataTable } from '../DataTable'
import {
  formatPricePerSqm,
  getChartColors,
  generateInsights,
  calculateKPIs,
  formatShortPricePerSqm,
  processRoomData,
} from './PriceM2PerRoomChart.utils'
import {
  PriceM2PerRoomChartProps,
  PriceM2PerRoomChartRef,
  KPIData,
  RoomPriceSqmData,
} from './PriceM2PerRoomChart.types'

const ColoredCell = styled.div<{
  $backgroundColor?: string
  $textColor?: string
}>`
  padding: 2px 4px;
  border-radius: 3px;
  background-color: ${(props) => props.$backgroundColor || 'transparent'};
  color: ${(props) => props.$textColor || 'inherit'};
  font-weight: ${(props) => (props.$backgroundColor ? '500' : 'inherit')};
  display: inline-block;
  min-width: 100%;
  text-align: center;
  font-size: 11px;
  line-height: 1.2;
`

const PRICE_TYPE_KEYS = ['median', 'average'] as const

// Default translation function that returns the last part of the key
const defaultT = (key: string): string => {
  const parts = key.split('.')
  return parts[parts.length - 1] || key
}

// Helper to translate property category names
const translateCategory = (
  category: string,
  t: (key: string) => string
): string => {
  const key = category.toLowerCase()
  if (key === 'apartment') return t('charts.categories.apartment')
  if (key === 'house') return t('charts.categories.house')
  if (key === 'all types' || key === 'all') return t('charts.labels.allTypes')
  return category
}

export const PriceM2PerRoomChart = forwardRef<
  PriceM2PerRoomChartRef,
  PriceM2PerRoomChartProps
>(
  (
    {
      data = [],
      loading = false,
      error = null,
      priceType = 'median',
      onPriceTypeChange,
      hidePriceTypeButtons = false,
      isRental = false,
      priceMode,
      showKPIs = true,
      showInsights = true,
      showControls = true,
      accentColor = '#00ade6',
      t = defaultT,
    },
    ref
  ) => {
    // Translation helpers
    const priceLabel = isRental
      ? t('charts.labels.rent')
      : t('charts.labels.price')
    const priceTypeLabel = t(`charts.priceTypes.${priceType}`)
    const perSqmLabel = '/m²'
    const scatterChartRef = useRef<HTMLDivElement>(null)
    const scatterInstance = useRef<echarts.ECharts | null>(null)
    const isPrintingRef = useRef(false)

    // Helper function to group rooms into categories
    const getRoomCategory = (rooms: number): string => {
      if (rooms <= 1.5) return '≤ 1.5'
      if (rooms <= 2.5) return '2 - 2.5'
      if (rooms <= 3.5) return '3 - 3.5'
      if (rooms <= 4.5) return '4 - 4.5'
      if (rooms <= 5.5) return '5 - 5.5'
      if (rooms <= 6.5) return '6 - 6.5'
      return '7+'
    }

    useEffect(() => {
      if (!loading && !error && processedData.length > 0) {
        createChart()
      }

      return () => {
        scatterInstance.current?.dispose()
      }
    }, [data, priceType, loading, error, priceMode])

    // Use ResizeObserver to properly resize chart when container size changes
    useEffect(() => {
      const resizeObserver = new ResizeObserver(() => {
        scatterInstance.current?.resize()
      })

      if (scatterChartRef.current) {
        resizeObserver.observe(scatterChartRef.current)
      }

      return () => resizeObserver.disconnect()
    }, [])

    // Process data
    const processedData = processRoomData(data, priceType)

    // Calculate and set KPIs
    const kpis = calculateKPIs(processedData, priceType)

    const kpiData: KPIData[] = [
      {
        id: 'total-listings',
        label: t('charts.kpis.totalListings'),
        value: kpis.totalListings.toLocaleString(),
        variant: 'default',
      },
      {
        id: 'avg-price-sqm',
        label: isRental
          ? t(`charts.kpis.${priceType}RentPerSqm`)
          : t(`charts.kpis.${priceType}PricePerSqm`),
        value: formatPricePerSqm(kpis.avgPricePerSqm, isRental, priceMode),
        variant: 'default',
      },
      {
        id: 'avg-rooms',
        label: t('charts.kpis.averageRooms'),
        value: kpis.avgRooms.toFixed(1),
        variant: 'default',
      },
      {
        id: 'price-per-sqm-per-room',
        label: isRental
          ? t(`charts.kpis.${priceType}RentPerSqmPerRoom`)
          : t(`charts.kpis.${priceType}PricePerSqmPerRoom`),
        value: formatPricePerSqm(
          kpis.avgPricePerSqmPerRoom,
          isRental,
          priceMode
        ),
        variant: 'default',
      },
    ]

    // Generate insights
    const insights = generateInsights(
      processedData,
      priceType,
      t,
      isRental,
      priceMode
    )

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      exportChart: () => {
        if (scatterInstance.current) {
          scatterInstance.current.resize()
          return scatterInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 3,
            backgroundColor: '#fff',
            excludeComponents: ['toolbox', 'dataZoom'],
          })
        }
        return null
      },
      getKPIs: () => kpiData,
      getInsights: () => insights,
      getTableData: () => {
        if (!processedData || processedData.length === 0) return []

        // Helper to convert rgba to solid RGB for PDF
        const getCountPastelColor = (
          value: number,
          minCount: number,
          maxCount: number
        ): { backgroundColor: string; textColor: string } => {
          if (minCount === maxCount)
            return { backgroundColor: '#f3f4f6', textColor: '#374151' }

          const position = (value - minCount) / (maxCount - minCount)
          const intensity = 0.1 + position * 0.2
          // Convert rgba(34, 197, 94, intensity) to solid RGB
          const r = Math.round(34 * intensity + 255 * (1 - intensity))
          const g = Math.round(197 * intensity + 255 * (1 - intensity))
          const b = Math.round(94 * intensity + 255 * (1 - intensity))
          return {
            backgroundColor: `rgb(${r}, ${g}, ${b})`,
            textColor: '#166534',
          }
        }

        // Group by category and rooms
        const grouped: { [key: string]: any[] } = {}
        processedData.forEach((item) => {
          const key = `${item.category}_${item.rooms}`
          if (!grouped[key]) {
            grouped[key] = []
          }
          grouped[key].push(item)
        })

        // Calculate quantiles for each group
        const calculateQuantiles = (values: number[]) => {
          const sorted = [...values].sort((a, b) => a - b)
          const getPercentile = (p: number) => {
            const index = (p / 100) * (sorted.length - 1)
            const lower = Math.floor(index)
            const upper = Math.ceil(index)
            const weight = index % 1
            return sorted[lower] * (1 - weight) + sorted[upper] * weight
          }

          return {
            q10: getPercentile(10),
            q30: getPercentile(30),
            q50: getPercentile(50),
            q70: getPercentile(70),
            q90: getPercentile(90),
          }
        }

        // Build table data
        const tableRows: any[] = []
        const priceField = priceType === 'median' ? 'medianPrice' : 'avgPrice'

        Object.entries(grouped).forEach(([_key, items]) => {
          const category = items[0].category
          const rooms = items[0].rooms
          const prices = items.map((item) => item[priceField])
          const count = items.reduce((sum, item) => sum + item.count, 0)
          const quantiles = calculateQuantiles(prices)

          tableRows.push({
            category,
            rooms,
            count,
            ...quantiles,
          })
        })

        // Add "All rooms" rows per category
        const categories = Array.from(
          new Set(processedData.map((d) => d.category))
        )
        categories.forEach((category) => {
          const categoryData = processedData.filter(
            (d) => d.category === category
          )
          const prices = categoryData.map((item) => item[priceField])
          const count = categoryData.reduce((sum, item) => sum + item.count, 0)
          const quantiles = calculateQuantiles(prices)

          tableRows.push({
            category,
            rooms: t('charts.labels.allRooms'),
            count,
            ...quantiles,
            isAllRooms: true,
          })
        })

        // Sort by category and rooms
        tableRows.sort((a, b) => {
          if (a.category !== b.category) {
            return a.category.localeCompare(b.category)
          }
          if (a.rooms === 'All rooms') return 1
          if (b.rooms === 'All rooms') return -1
          return a.rooms - b.rooms
        })

        // Calculate color intensity for count column
        const countValues = tableRows.map((d) => d.count)
        const minCount = Math.min(...countValues)
        const maxCount = Math.max(...countValues)

        // Format data for PDF export with solid RGB colors
        return tableRows.map((row) => {
          const countColor = getCountPastelColor(row.count, minCount, maxCount)
          const isAllRooms = row.rooms === 'All rooms'

          return {
            category: isAllRooms
              ? {
                  value: translateCategory(row.category, t),
                  backgroundColor: '#e0f2fe',
                  textColor: '#0c4a6e',
                }
              : translateCategory(row.category, t),
            rooms: isAllRooms
              ? {
                  value: row.rooms,
                  backgroundColor: '#e0f2fe',
                  textColor: '#0c4a6e',
                }
              : row.rooms.toString(),
            q10: isAllRooms
              ? {
                  value: formatShortPricePerSqm(row.q10, isRental, priceMode),
                  backgroundColor: '#e0f2fe',
                  textColor: '#0c4a6e',
                }
              : formatShortPricePerSqm(row.q10, isRental, priceMode),
            q30: isAllRooms
              ? {
                  value: formatShortPricePerSqm(row.q30, isRental, priceMode),
                  backgroundColor: '#e0f2fe',
                  textColor: '#0c4a6e',
                }
              : formatShortPricePerSqm(row.q30, isRental, priceMode),
            q50: {
              value: formatShortPricePerSqm(row.q50, isRental, priceMode),
              backgroundColor: isAllRooms ? '#e0f2fe' : '#fef3c7',
              textColor: isAllRooms ? '#0c4a6e' : '#92400e',
            },
            q70: isAllRooms
              ? {
                  value: formatShortPricePerSqm(row.q70, isRental, priceMode),
                  backgroundColor: '#e0f2fe',
                  textColor: '#0c4a6e',
                }
              : formatShortPricePerSqm(row.q70, isRental, priceMode),
            q90: isAllRooms
              ? {
                  value: formatShortPricePerSqm(row.q90, isRental, priceMode),
                  backgroundColor: '#e0f2fe',
                  textColor: '#0c4a6e',
                }
              : formatShortPricePerSqm(row.q90, isRental, priceMode),
            count: isAllRooms
              ? {
                  value: row.count.toLocaleString(),
                  backgroundColor: '#e0f2fe',
                  textColor: '#0c4a6e',
                }
              : {
                  value: row.count.toLocaleString(),
                  ...countColor,
                },
          }
        })
      },
    }))

    const createChart = () => {
      if (!scatterChartRef.current) return

      if (scatterInstance.current) {
        scatterInstance.current.dispose()
      }

      scatterInstance.current = safeChartInit(
        scatterChartRef.current,
        undefined,
        {
          renderer: 'canvas',
          devicePixelRatio: isPrintingRef.current
            ? 1
            : window.devicePixelRatio || 1,
        }
      )

      if (isPrintingRef.current) {
        setTimeout(() => {
          scatterInstance.current?.resize()
        }, 50)
      }

      const colors = getChartColors()
      const option = createScatterOption(colors)
      scatterInstance.current.setOption(option)
    }

    // Helper function to aggregate data by room category
    const aggregateByRoomGroup = (
      data: RoomPriceSqmData[],
      priceField: 'medianPrice' | 'avgPrice'
    ) => {
      const roomOrder = [
        '≤ 1.5',
        '2 - 2.5',
        '3 - 3.5',
        '4 - 4.5',
        '5 - 5.5',
        '6 - 6.5',
        '7+',
      ]
      const grouped: Record<string, RoomPriceSqmData[]> = {}

      data.forEach((d) => {
        const category = getRoomCategory(d.rooms)
        if (!grouped[category]) grouped[category] = []
        grouped[category].push(d)
      })

      return roomOrder
        .filter((cat) => grouped[cat]?.length > 0)
        .map((roomGroup) => {
          const items = grouped[roomGroup]
          const totalCount = items.reduce((sum, d) => sum + d.count, 0)
          return {
            roomGroup,
            price:
              items.reduce((sum, d) => sum + d[priceField] * d.count, 0) /
              totalCount,
            count: totalCount,
            min: Math.min(...items.map((d) => d.min)),
            max: Math.max(...items.map((d) => d.max)),
            stdDev: Math.sqrt(
              items.reduce(
                (sum, d) => sum + Math.pow(d.stdDev, 2) * d.count,
                0
              ) / totalCount
            ),
          }
        })
    }

    const createScatterOption = (colors: any) => {
      const apartmentData = processedData.filter(
        (d) => d.category === 'Apartment'
      )
      const houseData = processedData.filter((d) => d.category === 'House')
      const priceField = priceType === 'median' ? 'medianPrice' : 'avgPrice'

      // Aggregate data by room groups
      const aggregatedApartmentData = aggregateByRoomGroup(
        apartmentData,
        priceField
      )
      const aggregatedHouseData = aggregateByRoomGroup(houseData, priceField)

      // Get all room groups that have data (for x-axis categories)
      const roomOrder = [
        '≤ 1.5',
        '2 - 2.5',
        '3 - 3.5',
        '4 - 4.5',
        '5 - 5.5',
        '6 - 6.5',
        '7+',
      ]
      const allRoomGroups = new Set([
        ...aggregatedApartmentData.map((d) => d.roomGroup),
        ...aggregatedHouseData.map((d) => d.roomGroup),
      ])
      const xAxisCategories = roomOrder.filter((cat) => allRoomGroups.has(cat))

      // Calculate max count for visualMap
      const allAggregatedData = [
        ...aggregatedApartmentData,
        ...aggregatedHouseData,
      ]
      const maxCount =
        allAggregatedData.length > 0
          ? Math.max(...allAggregatedData.map((d) => d.count))
          : 1

      const option: any = {
        backgroundColor: 'transparent',
        title: {
          text: t('charts.titlesM2.roomCountVsPrice', {
            priceType: priceTypeLabel,
          }),
          left: 'center',
          top: 16,
          textStyle: {
            fontSize: 18,
            fontWeight: 600,
            color: '#1a1a1a',
          },
        },
        tooltip: {
          trigger: 'item',
          appendTo: () => document.body,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          borderRadius: 8,
          formatter: (params: any) => {
            const d = params.data
            return `
            <div style="padding: 12px;">
              <div style="font-weight: 600; margin-bottom: 8px;">${params.seriesName}</div>
              <div style="display: grid; gap: 4px;">
                <div>${t('charts.labels.rooms')}: ${d[0]}</div>
                <div>${priceTypeLabel} ${priceLabel}${perSqmLabel}: ${formatPricePerSqm(d[1], isRental, priceMode)}</div>
                <div>${t('charts.labels.listings')}: ${d[2].toLocaleString()}</div>
                <div>${t('charts.labels.range')}: ${formatPricePerSqm(d[3], isRental, priceMode)} - ${formatPricePerSqm(d[4], isRental, priceMode)}</div>
                <div>${t('charts.labels.stdDev')}: ${formatPricePerSqm(d[5], isRental, priceMode)}</div>
              </div>
            </div>
          `
          },
        },
        legend: {
          data: [
            t('charts.categories.apartment'),
            t('charts.categories.house'),
          ],
          bottom: 20,
          itemGap: 30,
          textStyle: {
            fontSize: 14,
            color: '#6c757d',
          },
        },
        grid: {
          left: 40,
          right: 10,
          top: 60,
          bottom: 110,
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: xAxisCategories,
          name: t('charts.labels.numberOfRooms'),
          nameLocation: 'middle',
          nameGap: 30,
          nameTextStyle: {
            fontSize: 14,
            color: '#6c757d',
          },
          axisLine: {
            lineStyle: {
              color: '#e5e7eb',
            },
          },
          axisLabel: {
            fontSize: 12,
            color: '#6c757d',
          },
          splitLine: {
            lineStyle: {
              color: '#f3f4f6',
              type: 'dashed',
            },
          },
        },
        yAxis: {
          type: 'value',
          name: `${priceTypeLabel} ${priceLabel}${perSqmLabel} (CHF)`,
          nameLocation: 'middle',
          nameGap: 60,
          nameTextStyle: {
            fontSize: 14,
            color: '#6c757d',
          },
          axisLine: {
            lineStyle: {
              color: '#e5e7eb',
            },
          },
          axisLabel: {
            fontSize: 12,
            color: '#6c757d',
            formatter: (value: number) =>
              formatShortPricePerSqm(value, isRental, priceMode),
          },
          splitLine: {
            lineStyle: {
              color: '#f3f4f6',
              type: 'dashed',
            },
          },
        },
        series: [
          {
            name: t('charts.categories.apartment'),
            type: 'scatter',
            data: aggregatedApartmentData.map((d) => [
              d.roomGroup,
              d.price,
              d.count,
              d.min,
              d.max,
              d.stdDev,
            ]),
            symbolSize: (data: number[]) => {
              return Math.sqrt(data[2]) * 3 + 10
            },
            itemStyle: {
              color: colors.apartment,
              opacity: 0.7,
            },
            emphasis: {
              itemStyle: {
                opacity: 1,
                shadowBlur: 10,
                shadowColor: 'rgba(37, 99, 235, 0.3)',
              },
            },
          },
          {
            name: t('charts.categories.house'),
            type: 'scatter',
            data: aggregatedHouseData.map((d) => [
              d.roomGroup,
              d.price,
              d.count,
              d.min,
              d.max,
              d.stdDev,
            ]),
            symbolSize: (data: number[]) => {
              return Math.sqrt(data[2]) * 3 + 10
            },
            itemStyle: {
              color: colors.house,
              opacity: 0.7,
            },
            emphasis: {
              itemStyle: {
                opacity: 1,
                shadowBlur: 10,
                shadowColor: 'rgba(16, 185, 129, 0.3)',
              },
            },
          },
        ],
        visualMap: {
          show: false,
          dimension: 2,
          min: 0,
          max: maxCount,
          inRange: {
            symbolSize: [10, 60],
          },
        },
      }

      // Add trend lines (using aggregated data with categorical x-axis)
      if (aggregatedApartmentData.length >= 2) {
        option.series.push({
          name: t('charts.categories.apartmentTrend'),
          type: 'line',
          data: aggregatedApartmentData.map((d) => [d.roomGroup, d.price]),
          smooth: true,
          symbol: 'none',
          lineStyle: {
            color: colors.apartment,
            width: 2,
            type: 'dashed',
          },
          silent: true,
        })
      }

      if (aggregatedHouseData.length >= 2) {
        option.series.push({
          name: t('charts.categories.houseTrend'),
          type: 'line',
          data: aggregatedHouseData.map((d) => [d.roomGroup, d.price]),
          smooth: true,
          symbol: 'none',
          lineStyle: {
            color: colors.house,
            width: 2,
            type: 'dashed',
          },
          silent: true,
        })
      }

      return option
    }

    useEffect(() => {
      // Match the Building Permits report, which prints cleanly: do NOT touch
      // the chart on print. The previous beforeprint/afterprint/matchMedia
      // handlers called chart.resize() *during* the print snapshot, which made
      // Chrome serialise the canvas as a masked vector figure (the white boxes
      // in macOS Preview / Quick Look). Building Permits charts do nothing on
      // print and rasterise cleanly. The print layout keeps the same width, so
      // no print-time resize is needed.
      const handleResize = () => {
        scatterInstance.current?.resize()
      }

      window.addEventListener('resize', handleResize)

      return () => {
        window.removeEventListener('resize', handleResize)
      }
    }, [])

    // Prepare quantile table data
    const renderQuantileTable = () => {
      const dataWithQuantiles = processedData.filter((d) => d.quantiles)

      if (dataWithQuantiles.length === 0) {
        return null
      }

      // Group data by category and room group
      const groupedData: { [key: string]: any[] } = {}

      dataWithQuantiles.forEach((d) => {
        const roomGroup = getRoomCategory(d.rooms)
        const key = `${d.category}_${roomGroup}`

        if (!groupedData[key]) {
          groupedData[key] = []
        }
        groupedData[key].push(d)
      })

      // Calculate aggregated quantiles for each group
      const aggregatedData = Object.entries(groupedData).map(([key, items]) => {
        const [category, roomGroup] = key.split('_')
        const totalCount = items.reduce((sum, item) => sum + item.count, 0)

        // Calculate weighted quantiles
        const q10Values = items.map((item) => item.quantiles.q10 * item.count)
        const q30Values = items.map((item) => item.quantiles.q30 * item.count)
        const q50Values = items.map((item) => item.quantiles.q50 * item.count)
        const q70Values = items.map((item) => item.quantiles.q70 * item.count)
        const q90Values = items.map((item) => item.quantiles.q90 * item.count)

        return {
          category,
          roomGroup,
          q10: Math.round(q10Values.reduce((a, b) => a + b, 0) / totalCount),
          q30: Math.round(q30Values.reduce((a, b) => a + b, 0) / totalCount),
          q50: Math.round(q50Values.reduce((a, b) => a + b, 0) / totalCount),
          q70: Math.round(q70Values.reduce((a, b) => a + b, 0) / totalCount),
          q90: Math.round(q90Values.reduce((a, b) => a + b, 0) / totalCount),
          count: totalCount,
        }
      })

      // Add "All rooms" row for each category
      const categories = [...new Set(dataWithQuantiles.map((d) => d.category))]
      const allRoomsData = categories.map((category) => {
        const categoryItems = dataWithQuantiles.filter(
          (d) => d.category === category
        )
        const totalCount = categoryItems.reduce(
          (sum, item) => sum + item.count,
          0
        )

        const q10 = Math.round(
          categoryItems.reduce(
            (sum, item) => sum + (item?.quantiles?.q10 || 0) * item.count,
            0
          ) / totalCount
        )
        const q30 = Math.round(
          categoryItems.reduce(
            (sum, item) => sum + (item?.quantiles?.q30 || 0) * item.count,
            0
          ) / totalCount
        )
        const q50 = Math.round(
          categoryItems.reduce(
            (sum, item) => sum + (item?.quantiles?.q50 || 0) * item.count,
            0
          ) / totalCount
        )
        const q70 = Math.round(
          categoryItems.reduce(
            (sum, item) => sum + (item?.quantiles?.q70 || 0) * item.count,
            0
          ) / totalCount
        )
        const q90 = Math.round(
          categoryItems.reduce(
            (sum, item) => sum + (item?.quantiles?.q90 || 0) * item.count,
            0
          ) / totalCount
        )

        return {
          category,
          roomGroup: t('charts.labels.allRooms'),
          q10,
          q30,
          q50,
          q70,
          q90,
          count: totalCount,
        }
      })

      const allRoomsLabel = t('charts.labels.allRooms')
      // Combine and sort the data
      const finalData = [...allRoomsData, ...aggregatedData].sort((a, b) => {
        // Sort by category first
        if (a.category !== b.category) {
          return a.category.localeCompare(b.category)
        }
        // Then by room group (All rooms first)
        if (a.roomGroup === allRoomsLabel) return -1
        if (b.roomGroup === allRoomsLabel) return 1

        // Sort room groups in order
        const roomOrder = [
          '≤ 1.5',
          '2 - 2.5',
          '3 - 3.5',
          '4 - 4.5',
          '5 - 5.5',
          '6 - 6.5',
          '7+',
        ]
        return roomOrder.indexOf(a.roomGroup) - roomOrder.indexOf(b.roomGroup)
      })

      const quantileData = finalData.map((d) => ({
        category: translateCategory(d.category, t),
        rooms: d.roomGroup,
        q10: d.q10,
        q30: d.q30,
        q50: d.q50,
        q70: d.q70,
        q90: d.q90,
        count: d.count,
        isAllRooms: d.roomGroup === allRoomsLabel,
      }))

      // Calculate color intensity for count column
      const countValues = quantileData.map((d) => d.count)
      const minCount = Math.min(...countValues)
      const maxCount = Math.max(...countValues)

      const getCountColor = (value: number) => {
        const position = (value - minCount) / (maxCount - minCount)
        const intensity = 0.1 + position * 0.2
        return {
          backgroundColor: `rgba(34, 197, 94, ${intensity})`,
          textColor: '#166534',
        }
      }

      const columns = [
        {
          key: 'category',
          label: t('charts.labels.propertyType'),
          sortable: true,
          render: (item: any) => (
            <ColoredCell
              $backgroundColor={item.isAllRooms ? '#e0f2fe' : undefined}
              $textColor={item.isAllRooms ? '#0c4a6e' : undefined}
            >
              {item.category}
            </ColoredCell>
          ),
        },
        {
          key: 'rooms',
          label: t('charts.labels.rooms'),
          sortable: true,
          render: (item: any) => (
            <ColoredCell
              $backgroundColor={item.isAllRooms ? '#e0f2fe' : undefined}
              $textColor={item.isAllRooms ? '#0c4a6e' : undefined}
            >
              {item.rooms}
            </ColoredCell>
          ),
        },
        {
          key: 'q10',
          label: t('charts.labels.percentile10'),
          sortable: true,
          render: (item: any) => (
            <ColoredCell
              $backgroundColor={item.isAllRooms ? '#e0f2fe' : undefined}
              $textColor={item.isAllRooms ? '#0c4a6e' : undefined}
            >
              {formatShortPricePerSqm(item.q10, isRental, priceMode)}
            </ColoredCell>
          ),
        },
        {
          key: 'q30',
          label: t('charts.labels.percentile30'),
          sortable: true,
          render: (item: any) => (
            <ColoredCell
              $backgroundColor={item.isAllRooms ? '#e0f2fe' : undefined}
              $textColor={item.isAllRooms ? '#0c4a6e' : undefined}
            >
              {formatShortPricePerSqm(item.q30, isRental, priceMode)}
            </ColoredCell>
          ),
        },
        {
          key: 'q50',
          label: t('charts.labels.percentile50Median'),
          sortable: true,
          render: (item: any) => (
            <ColoredCell
              $backgroundColor={item.isAllRooms ? '#e0f2fe' : '#fef3c7'}
              $textColor={item.isAllRooms ? '#0c4a6e' : '#92400e'}
            >
              {formatShortPricePerSqm(item.q50, isRental, priceMode)}
            </ColoredCell>
          ),
        },
        {
          key: 'q70',
          label: t('charts.labels.percentile70'),
          sortable: true,
          render: (item: any) => (
            <ColoredCell
              $backgroundColor={item.isAllRooms ? '#e0f2fe' : undefined}
              $textColor={item.isAllRooms ? '#0c4a6e' : undefined}
            >
              {formatShortPricePerSqm(item.q70, isRental, priceMode)}
            </ColoredCell>
          ),
        },
        {
          key: 'q90',
          label: t('charts.labels.percentile90'),
          sortable: true,
          render: (item: any) => (
            <ColoredCell
              $backgroundColor={item.isAllRooms ? '#e0f2fe' : undefined}
              $textColor={item.isAllRooms ? '#0c4a6e' : undefined}
            >
              {formatShortPricePerSqm(item.q90, isRental, priceMode)}
            </ColoredCell>
          ),
        },
        {
          key: 'count',
          label: t('charts.labels.count'),
          sortable: true,
          render: (item: any) => {
            const countColor = getCountColor(item.count)
            return (
              <ColoredCell
                $backgroundColor={
                  item.isAllRooms ? '#e0f2fe' : countColor.backgroundColor
                }
                $textColor={item.isAllRooms ? '#0c4a6e' : countColor.textColor}
              >
                {item.count.toLocaleString()}
              </ColoredCell>
            )
          },
        },
      ]

      return (
        <>
          <Text
            variant="body2"
            weight="semibold"
            style={{ marginBottom: '8px' }}
          >
            {isRental
              ? t('charts.titlesM2.rentDistributionByRooms')
              : t('charts.titlesM2.priceDistributionByRooms')}
          </Text>
          <DataTable
            data={quantileData}
            columns={columns}
            stickyHeader={false}
            compact={true}
            dense={true}
          />
        </>
      )
    }

    if (loading) {
      return (
        <Box style={{ textAlign: 'center', padding: '40px' }}>
          <Loading variant="spinner" size="large" center />
          <Text variant="body1" color="secondary" style={{ marginTop: '16px' }}>
            {t('charts.states.loadingPriceM2PerRoom')}
          </Text>
        </Box>
      )
    }

    if (error) {
      return (
        <ErrorContainer variant="error">
          <Text variant="body1">{error}</Text>
        </ErrorContainer>
      )
    }

    if (processedData.length === 0) {
      return (
        <NotAvailable
          title={t('charts.empty.title')}
          description={
            isRental
              ? t('charts.empty.rentPerSqmPerRoom')
              : t('charts.empty.pricePerSqmPerRoom')
          }
        />
      )
    }

    return (
      <PrintWrapper>
        {showControls && onPriceTypeChange && !hidePriceTypeButtons && (
          <Box marginBottom="16px">
            <Box display="flex" justifyContent="flex-end">
              <ButtonGroup>
                {PRICE_TYPE_KEYS.map((ptKey) => (
                  <Button
                    key={ptKey}
                    variant={priceType === ptKey ? 'primary' : 'secondary'}
                    size="small"
                    onClick={() => onPriceTypeChange(ptKey)}
                  >
                    {t(`charts.priceTypes.${ptKey}`)}
                  </Button>
                ))}
              </ButtonGroup>
            </Box>
          </Box>
        )}

        {showKPIs && (
          <Box marginBottom="30px">
            <StatsContainer fullWidth stats={kpiData} />
          </Box>
        )}

        {/* Chart and Quantile Table side by side */}
        {processedData &&
        processedData.length > 0 &&
        processedData.some((d) => d.quantiles) ? (
          <ChartGrid columns={2}>
            <ChartSection>
              <ChartContainer height={480} ref={scatterChartRef} />
            </ChartSection>
            <ChartSection>
              <Box>{renderQuantileTable()}</Box>
            </ChartSection>
          </ChartGrid>
        ) : (
          <Box marginBottom="30px">
            <ChartSection>
              <ChartContainer height={480} ref={scatterChartRef} />
            </ChartSection>
          </Box>
        )}

        {showInsights && insights.length > 0 && (
          <Box>
            <ChartTitle style={{ marginBottom: '24px' }}>
              {t('charts.insights.keyInsights')}
            </ChartTitle>
            <Box display="flex" flexWrap="wrap" gap="24px">
              {insights.map((insight, index) => (
                <Box
                  key={index}
                  flex="1 1 280px"
                  style={{
                    background: '#f8f9fa',
                    borderRadius: '12px',
                    padding: '20px',
                    borderLeft: `4px solid ${accentColor}`,
                    minWidth: 0,
                  }}
                >
                  <Text
                    variant="h4"
                    style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      marginBottom: '8px',
                      color: '#1a1a1a',
                    }}
                  >
                    {insight.title}
                  </Text>
                  <Text
                    variant="body2"
                    style={{
                      fontSize: '14px',
                      lineHeight: 1.5,
                      color: '#6c757d',
                    }}
                  >
                    {insight.description}
                  </Text>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </PrintWrapper>
    )
  }
)

PriceM2PerRoomChart.displayName = 'PriceM2PerRoomChart'
